import Phaser from 'phaser';
import { SCENE_KEYS } from '../config';
import { PlayerLogic } from '../logic/player-logic';
import { createPlayerLogicState } from '../logic/player-state';
import { CONSTANTS } from '../constants';
import { EnemyLogic } from '../logic/enemy-logic';
import { createEnemyLogicState } from '../logic/enemy-state';
import { Wallet } from '../systems/WalletSystem';
import { ScoreSystem } from '../systems/ScoreSystem';
import { Mission, type GameState } from '../systems/MissionSystem';
import { InteractionSystem } from '../systems/InteractionSystem';
import { EffectSystem } from '../systems/EffectSystem';
import { SoundManager } from '../audio/SoundManager';
import { CharacterRenderer } from '../rendering/CharacterRenderer';
import { PowerupRenderer } from '../rendering/PowerupRenderer';
import { Npc } from '../entities/Npc';
import { HudRenderer } from '../rendering/HudRenderer';
import { level1Data } from '../levels/level1';
import { POWERUP_DATA } from '../data/powerups';
import { aabbOverlap } from '../utils/MathUtils';
import { Checkpoint } from '../systems/CheckpointSystem';
import { LevelSystem } from '../systems/LevelSystem';
import { CombatSystem, type ICombatSystem } from '../systems/CombatSystem';
import { TouchControls } from '../systems/TouchControls';

export class GameScene extends Phaser.Scene {
  private playerLogic!: PlayerLogic;
  private playerSprite!: Phaser.GameObjects.Sprite;
  private statusText!: Phaser.GameObjects.Text;
  private keys!: Phaser.Types.Input.Keyboard.CursorKeys;
  private jumpKey!: Phaser.Input.Keyboard.Key;
  private stoneKey!: Phaser.Input.Keyboard.Key;

  // Systems
  private wallet!: Wallet;
  private score!: ScoreSystem;
  private missions!: Mission[];
  private interactionSystem!: InteractionSystem;
  private effectSystem!: EffectSystem;
  private soundManager!: SoundManager;
  private characterRenderer!: CharacterRenderer;
  private powerupRenderer!: PowerupRenderer;
  private hudRenderer!: HudRenderer;
  private levelSystem!: LevelSystem;
  private combatSystem!: ICombatSystem;

  // Background layers
  // Background tileSprites keyed by layer key, for parallax update
  private bgLayers = new Map<string, { sprite: Phaser.GameObjects.TileSprite; rate: number }>();

  // Entities
  private enemies: Array<{ logic: EnemyLogic; sprite: Phaser.GameObjects.Sprite; data: { type: string; x: number; y: number }; stoneKillable?: boolean }> = [];
  private npcs: Array<{ npc: import('../entities/Npc').Npc; sprite: Phaser.GameObjects.Sprite; stoneKillable?: boolean; bridge?: boolean; fallsInSea?: boolean; sinking?: boolean }> = [];
  private powerups: Array<{ type: string; x: number; y: number; width?: number; height?: number; worldY: number; collected: boolean; powerupData: import('../data/powerups').PowerupDefinition; data?: import('../data/powerups').PowerupEffect; sprite: Phaser.GameObjects.Sprite }> = [];
  private checkpoints: Checkpoint[] = [];
  private exitTriggered: boolean = false;

  // Touch controls (GDD §18)
  private touchControls!: TouchControls;
  private worldW: number = CONSTANTS.WORLD_WIDTH;

  constructor() { super({ key: SCENE_KEYS.GAME }); }

  private applyAdaptiveZoom(): void {
    const { width, height } = this.scale.gameSize;
    const zoom = height / CONSTANTS.CANVAS_HEIGHT;
    this.cameras.main.setZoom(zoom);
    this.cameras.main.setBounds(0, 0, this.worldW, CONSTANTS.CANVAS_HEIGHT);
    this.physics.world.setBounds(0, 0, this.worldW, CONSTANTS.CANVAS_HEIGHT);
    const inv = 1 / zoom;
    this.hudRenderer?.resize(width, inv);
    if (this.statusText) {
      this.statusText.setScale(inv);
      this.statusText.setPosition(10 * inv, 60 * inv);
    }
  }

  private createPlatformSprite(
    x: number, y: number, w: number, h: number, key: string,
    mode: 'tile' | 'stretch' = 'stretch',
  ): Phaser.GameObjects.GameObject {
    // Verify texture exists AND is a real loaded image (not __DEFAULT/__MISSING/broken)
    const tex = this.textures.exists(key) ? this.textures.get(key) : null;
    const src = tex ? (tex.getSourceImage() as HTMLImageElement | undefined) : undefined;
    const valid = !!tex
      && tex.key !== '__DEFAULT'
      && tex.key !== '__MISSING'
      && !!src
      && src.width > 0;

    if (!valid) {
      console.warn(`[GameScene] texture "${key}" missing — using green placeholder`);
    }

    if (valid) {
      if (mode === 'tile') {
        return this.add.tileSprite(x, y, w, h, key).setOrigin(0, 0).setScrollFactor(1);
      }
      const img = this.add.image(x + w / 2, y + h / 2, key).setScrollFactor(1);
      img.setDisplaySize(w, h);
      return img;
    }
    return this.add.rectangle(x + w / 2, y + h / 2, w, h, 0x00ff00, 0.6).setScrollFactor(1);
  }

  create(): void {
    // Reference level the world uses; character relative y is measured from here.
    const GROUND_Y = level1Data.groundY;
    // Convert a character's relative y (0 = floor, positive = up) to world px.
    const toWorldY = (relY: number) => GROUND_Y - relY;

    // World — per-level width from level data (fallback: CONSTANTS.WORLD_WIDTH)
    const worldW = ((level1Data as unknown as { width?: number }).width) ?? CONSTANTS.WORLD_WIDTH;
    this.worldW = worldW;
    this.physics.world.setBounds(0, 0, worldW, CONSTANTS.CANVAS_HEIGHT);
    this.applyAdaptiveZoom();

    // === BACKGROUND (parallax layers) — A2: scrollFactor 1 anclado al mundo
    const BG_H = 384;
    let depth = -10;
    level1Data.backgroundLayers.forEach(layer => {
      const worldBottom = GROUND_Y - layer.y;
      let sprite: Phaser.GameObjects.TileSprite;
      if (this.textures.exists(layer.key)) {
        sprite = this.add.tileSprite(0, worldBottom, worldW, BG_H, layer.key)
          .setOrigin(0, 1).setScrollFactor(1).setDepth(depth);
      } else {
        const rect = this.add.rectangle(0, worldBottom, worldW, BG_H, 0x00ff00, 0.3)
          .setOrigin(0, 1).setScrollFactor(1).setDepth(depth);
        sprite = rect as unknown as Phaser.GameObjects.TileSprite;
      }
      this.bgLayers.set(layer.key, { sprite, rate: layer.parallaxX });
      depth++;
    });

    // Ground platforms
    level1Data.groundPlatforms.forEach(p => {
      const plat = this.createPlatformSprite(p.x, toWorldY(p.y), p.width, p.height, 'platform-ground');
      this.physics.add.existing(plat, true);
    });

    // Floating platforms — type determines the sprite variant, render determines tile/stretch
    level1Data.floatingPlatforms.forEach(p => {
      const plat = this.createPlatformSprite(p.x, toWorldY(p.y), p.width, p.height, p.type, p.render ?? 'stretch');
      this.physics.add.existing(plat, true);
    });

    // Sea water in the gaps between ground segments (y = GRCOUND_Y, height 40).
    // The player can fall into these — the water marks where the "streets" are cut.
    const groundPlats = (level1Data.groundPlatforms as unknown as Array<{ x: number; y: number; width: number; height: number; isStatic: boolean }>)
      .filter(p => p.height === 40)
      .sort((a, b) => a.x - b.x);
    for (let i = 0; i < groundPlats.length - 1; i++) {
      const a = groundPlats[i];
      const b = groundPlats[i + 1];
      const gapLeft = a.x + a.width;
      const gapRight = b.x;
      if (gapRight > gapLeft) {
        this.createPlatformSprite(gapLeft, toWorldY(a.y) + 20, gapRight - gapLeft, 20, 'sea-tile', 'tile');
      }
    }

    // === GOAL / EXIT MARKER ===
    const exitX = level1Data.exitX;
    const goalFlag = this.add.sprite(exitX, GROUND_Y - 80, 'exit_goal').setOrigin(0.5, 1).setDepth(2);
    this.tweens.add({ targets: goalFlag, yoyo: true, repeat: -1, duration: 900, angle: { from: -4, to: 4 } });
    this.add.text(exitX, GROUND_Y - 110, 'MALECÓN', {
      fontSize: '14px', fontFamily: 'monospace', color: '#ffdd00', backgroundColor: '#00000088', padding: { x: 4, y: 2 },
    }).setOrigin(0.5, 0).setDepth(2);

    // === SYSTEMS ===
    this.wallet = new Wallet();
    this.score = new ScoreSystem();
    this.missions = level1Data.missions.map(m => new Mission(m.id, m.type, m.target));
    this.interactionSystem = new InteractionSystem();
    this.effectSystem = new EffectSystem(this);
    this.soundManager = new SoundManager(this);
    this.characterRenderer = new CharacterRenderer(this);
    this.powerupRenderer = new PowerupRenderer(this);
    this.hudRenderer = new HudRenderer(this);
    this.hudRenderer.onToggleMute = () => this.soundManager.toggleMute();
    this.hudRenderer.setMuteVisual(this.soundManager.isMuted);
    this.applyAdaptiveZoom();
    // Per-level music track (falls back to the default loop)
    const levelMusic = (level1Data as unknown as { music?: string }).music;
    if (levelMusic) this.soundManager.setMusicKey(levelMusic);
    // Start now if the user already interacted (assets are loaded at this point);
    // otherwise the global input handler retries on the next interaction.
    this.soundManager.playMusic();
    this.levelSystem = new LevelSystem();
    this.combatSystem = new CombatSystem();
    this.levelSystem.loadLevel(level1Data.id, level1Data as unknown as import('../levels/LevelDefinition').LevelDefinition);

    // === PLAYER ===
    const initState = createPlayerLogicState(level1Data.startPlayerX, toWorldY(level1Data.startPlayerY));
    initState.stones = (level1Data as unknown as { stones?: number }).stones ?? 10;
    initState.onGround = true;
    this.playerLogic = new PlayerLogic(initState);
    this.playerSprite = this.characterRenderer.createPlayerSprite(
      level1Data.startPlayerX,
      toWorldY(level1Data.startPlayerY)
    );
    this.cameras.main.startFollow(this.playerSprite, true, 0.5, 0.5);
    this.cameras.main.setBounds(0, 0, worldW, CONSTANTS.CANVAS_HEIGHT);

    // === CHECKPOINTS ===
    this.checkpoints = level1Data.checkpoints.map((c, i) => {
      const cpW = (c as any).width ?? 40;
      const cpH = (c as any).height ?? 80;
      const cp = new Checkpoint(c.x, toWorldY(c.y));
      const cpKey = `checkpoint-${i + 1}`;
      const cpTex = this.textures.exists(cpKey) ? cpKey : 'checkpoint';
      const cpSprite = this.add.sprite(c.x, toWorldY(c.y) - cpH / 2, cpTex);
      cpSprite.setDisplaySize(cpW, cpH);
      return cp;
    });

    // === ENEMIES ===
    const enemySpawns = level1Data.enemies as unknown as import('../levels/LevelDefinition').EnemySpawn[];
    this.enemies = enemySpawns.map(e => {
      const logic = new EnemyLogic(createEnemyLogicState(e.x, toWorldY(e.y), e.type as 'pcc' | 'fmc' | 'ujc'));
      // Apply per-spawn overrides from level data
      const overrides: Record<string, unknown> = {};
      if (e.speed !== undefined) overrides.speed = e.speed;
      if (e.patrolRange !== undefined) overrides.patrolRange = e.patrolRange;
      if (e.detectionRange !== undefined) overrides.detectionRange = e.detectionRange;
      if (e.attackRange !== undefined) overrides.attackRange = e.attackRange;
      if (e.hp !== undefined) { overrides.hp = e.hp; overrides.maxHp = e.hp; }
      if (e.damage !== undefined) overrides.damage = e.damage;
      if (e.hasBat !== undefined) overrides.hasBat = e.hasBat;
      overrides.canLeaveWorld = e.canLeaveWorld === true;
      overrides.worldMinX = e.minX ?? 0;
      overrides.worldMaxX = e.maxX ?? worldW;
      logic.setState(overrides);
      const sprite = this.characterRenderer.createEnemySprite(e.x, toWorldY(e.y), e.type, e.spriteKey);
      return { logic, sprite, data: e, stoneKillable: e.stoneKillable ?? true };
    });

    // === NPCs ===
    const npcSpawns = level1Data.npcs as unknown as import('../levels/LevelDefinition').NpcSpawn[];
    this.npcs = npcSpawns.map(n => {
      const worldX = n.x;
      const worldY = toWorldY(n.y);
      const npc = new Npc({ ...(n.data as any), x: worldX, y: worldY, canLeaveWorld: n.canLeaveWorld ?? false });
      const sprite = this.characterRenderer.renderNpc(n.type, worldX, worldY, n.data?.category !== 'ambient', n.spriteKey);
      // Apply per-spawn patrol overrides from level data
      if (n.canLeaveWorld) {
        npc.patrolMinX = -200;
        npc.patrolMaxX = worldW + 200;
      } else {
        const strip = n.patrolRange ?? 40;
        npc.patrolMinX = Math.max(0, worldX - strip);
        npc.patrolMaxX = Math.min(worldW, worldX + strip);
      }
      npc.patrolSpeed = n.patrolSpeed ?? (0.3 + (worldX % 4) * 0.15);
      return { npc, sprite, stoneKillable: n.stoneKillable ?? true, bridge: n.bridge ?? false, fallsInSea: n.fallsInSea ?? false };
    });

    // === POWERUPS ===
    this.powerups = level1Data.powerups.map(p => {
      const puData = POWERUP_DATA[p.type];
      const key = `powerup_${p.type}`;
      const worldY = toWorldY(p.y);
      const pw = p.width ?? puData.width;
      const ph = p.height ?? puData.height;
      let sprite: Phaser.GameObjects.Sprite;
      if (this.textures.exists(key)) {
        sprite = this.add.sprite(p.x, worldY, key);
        const src = this.textures.get(key).getSourceImage() as HTMLImageElement;
        sprite.setScale(pw / src.width, ph / src.height);
      } else {
        // Green placeholder
        const rect = this.add.rectangle(p.x, worldY, pw, ph, 0x00ff00, 0.8);
        sprite = rect as unknown as Phaser.GameObjects.Sprite;
      }
      return { ...p, worldY, collected: false, powerupData: puData, data: p.data, width: pw, height: ph, sprite };
    });

    // === INPUT ===
    this.keys = this.input.keyboard!.createCursorKeys();
    this.jumpKey = this.input.keyboard!.addKey('SPACE');
    this.jumpKey.on('up', () => this.playerLogic.releaseJump());
    this.stoneKey = this.input.keyboard!.addKey('ENTER');
    // Fullscreen toggle (desktop F, also available via touch button)
    const fsKey = this.input.keyboard!.addKey('F');
    fsKey.on('down', () => {
      const avail = (this.scale as unknown as { fullscreen: { available: boolean } }).fullscreen.available;
      if (!avail) return;
      try {
        if (this.scale.isFullscreen) this.scale.stopFullscreen();
        else this.scale.startFullscreen();
      } catch { /* ignore */ }
    });
    // Debug: T to force show/hide touch controls (persists in localStorage)
    const tKey = this.input.keyboard!.addKey('T');
    tKey.on('down', () => {
      try {
        const cur = localStorage.getItem('gz_forceTouch');
        const next = cur === '1' ? '0' : '1';
        localStorage.setItem('gz_forceTouch', next);
        this.touchControls?.refreshVisibility();
        console.log(`[TouchControls] force ${next === '1' ? 'ON' : 'OFF'} (reload or T again)`);
      } catch { /* ignore */ }
    });
    // Hint in console for Chrome emulation
    try {
      const should = this.touchControls.shouldShow();
      console.log(`[TouchControls] shouldShow=${should} touch=${(this.sys.game.device.input as unknown as {touch:boolean}).touch} hasTouch=${'ontouchstart' in window} maxPoints=${navigator.maxTouchPoints} coarse=${window.matchMedia?.('(pointer: coarse)').matches} w=${window.innerWidth}`);
      if (!should) console.log('[TouchControls] Controles ocultos en desktop. Pulsa T o usa ?mobile=1 para forzarlos. ?desktop=1 para ocultarlos.');
    } catch { /* ignore */ }

    this.input.on('pointerdown', () => {
      try { this.soundManager.unlock(); } catch { /* no audio */ }
    });
    this.input.keyboard?.on('keydown', () => {
      try { this.soundManager.unlock(); } catch { /* no audio */ }
    });

    // Status text
    this.statusText = this.add.text(10, 60, '', {
      fontSize: '11px', fontFamily: 'monospace', color: '#ffffff',
      backgroundColor: '#00000088', padding: { x: 4, y: 2 },
    }).setScrollFactor(0).setDepth(10);

    // === TOUCH CONTROLS (GDD §18) ===
    this.touchControls = new TouchControls(this);
    this.touchControls.create();
  }

  update(_time: number, delta: number): void {
    const GROUND_Y = level1Data.groundY;

    // === 1. Player movement (keyboard + touch GDD §18) ===
    const keyboardDir = (this.keys.right?.isDown ? 1 : 0) - (this.keys.left?.isDown ? 1 : 0);
    const touchDir = this.touchControls?.getMoveDirection() ?? 0;
    const moveDirection = keyboardDir !== 0 ? keyboardDir : touchDir;
    const isSprinting = (this.keys.shift?.isDown || false) || (this.touchControls?.isSprintHeld ?? false);
    this.playerLogic.move(moveDirection, isSprinting);

    // Get player state early for stone throw
    const playerState = this.playerLogic.getState();

    // === 2. Jump (SPACE or touch SALTO) ===
    const touchJumpPress = this.touchControls?.consumeJumpPressed() ?? false;
    const touchJumpRelease = this.touchControls?.consumeJumpReleased() ?? false;
    if (touchJumpRelease) this.playerLogic.releaseJump();
    if (Phaser.Input.Keyboard.JustDown(this.jumpKey) || touchJumpPress) {
      const st = this.playerLogic.getState();
      if (st.onGround) {
        this.playerLogic.jump(200);
        this.soundManager.playJump();
      } else {
        if (this.playerLogic.doubleJump()) {
          this.soundManager.playDoubleJump();
        }
      }
    }

    // === 3. Stone throw (ENTER or touch TIRO) ===
    const touchStonePress = this.touchControls?.consumeStonePressed() ?? false;
    if (Phaser.Input.Keyboard.JustDown(this.stoneKey) || touchStonePress) {
      if (this.playerLogic.throwStone()) {
        this.soundManager.playStoneThrow();
        this.combatSystem.spawnStone(playerState, this.playerLogic, this);
      }
    }

    // === 4. Physics ===
    this.playerLogic.update(delta);

    // === 5. Ground collision ===
    const state = this.playerLogic.getState();
    // Only lands on the ground platform if there is actually ground below;
    // over a gap (sea water hole) the player keeps falling into the water.
    const groundBelow = level1Data.groundPlatforms
      .some(p => state.x >= p.x && state.x <= p.x + p.width);
    if (groundBelow && state.y >= GROUND_Y) {
      this.playerLogic.setOnGround(true);
      this.playerLogic.setState({ x: state.x, y: GROUND_Y, vy: 0 });
    } else {
      this.playerLogic.setOnGround(false);
    }

    // === 5b. Platform collision ===
    // Dead bridge-NPCs lying over a gap act as solid bodies the player can cross
    const bridgePlats = this.npcs
      .filter(n => n.bridge && n.npc.killed && !n.sinking)
      .map(n => ({ x: n.npc.x - 45, y: GROUND_Y - 18, width: 90, height: 18 }));
    const allPlatforms = [
      ...level1Data.groundPlatforms,
      ...level1Data.floatingPlatforms,
    ].map(p => ({ x: p.x, y: GROUND_Y - p.y, width: p.width, height: p.height }));
    this.playerLogic.checkPlatforms([...allPlatforms, ...bridgePlats]);

    // === 6. Pit check ===
    this.playerLogic.checkPit(CONSTANTS.PIT_THRESHOLD);

    // === 7. Enemies AI + Physics + Ground collision + Visual sync ===
    this.enemies.forEach(e => {
      e.logic.updateAI(playerState, delta);
      e.logic.updatePhysics(delta);

      // Enemy ground collision (stand on the ground/platform like the player)
      const eSt = e.logic.getState();
      if (eSt.y >= GROUND_Y) {
        e.logic.setOnGround(true);
        e.logic.setState({ y: GROUND_Y, vy: 0 });
      } else {
        e.logic.setOnGround(false);
      }
      // Visual sync (bottom-aligned on the state feet position, like the player)
      const enemyState = e.logic.getState();
      this.characterRenderer.updateEnemy(e.sprite, {
        x: enemyState.x,
        y: enemyState.y,
        facing: enemyState.facing,
        state: enemyState.state,
        vx: enemyState.vx,
        vy: enemyState.vy,
      });

      // === ENEMY ATTACKS PLAYER ===
      if (e.logic.canAttackPlayer(playerState) && !playerState.isInvulnerable) {
        const killed = this.playerLogic.takeDamage(e.logic.getAttackDamage());
        // Knockback (GDD 5.2)
        const knockDir = playerState.x < e.logic.getState().x ? -1 : 1;
        this.playerLogic.setState({ vx: knockDir * 2, vy: -2 });
        this.soundManager.playHit();
        this.effectSystem.screenShake(0.05, 250);
        this.effectSystem.spawnParticles(this.playerSprite.x, this.playerSprite.y - 20, 6, 0xff4444);
        // Comic hit phrase: enemy's own lines, falling back to the level pool
        const eSpawn = e.data as unknown as { phrases?: string[] };
        const levelPool = (level1Data as unknown as { enemyHitPhrases?: string[] }).enemyHitPhrases;
        const pool = eSpawn.phrases?.length ? eSpawn.phrases : levelPool;
        if (pool?.length) {
          const phrase = Phaser.Utils.Array.GetRandom(pool);
          this.powerupRenderer.spawnHitPhrase(this.playerSprite.x, this.playerSprite.y - 80, phrase);
        }
        if (killed) {
          this.soundManager.playDefeat();
          this.scene.start('DefeatScene', { title: (level1Data as any).defeatTitle, text: (level1Data as any).defeatText });
          return;
        }
      }
    });

    // === ENEMY SEPARATION — push overlapping enemies apart so they don't stack ===
    for (let i = 0; i < this.enemies.length; i++) {
      for (let j = i + 1; j < this.enemies.length; j++) {
        const a = this.enemies[i].logic.getState();
        const b = this.enemies[j].logic.getState();
        if (a.state === 'dead' || b.state === 'dead') continue;
        const minDist = 60; // ~enemy sprite width
        const dx = b.x - a.x;
        const dist = Math.abs(dx);
        if (dist < minDist) {
          const push = (minDist - dist) / 2;
          const dir = dx >= 0 ? 1 : -1;
          this.enemies[i].logic.setState({ x: a.x - dir * push });
          this.enemies[j].logic.setState({ x: b.x + dir * push });
        }
      }
    }

    // === ENEMY COMBAT (stone + stomp) — once per frame ===
    this.combatSystem.checkStoneEnemyCollision(
      this.enemies, this.score, this.effectSystem, this.soundManager, this.playerLogic, this, level1Data.scoreStoneKill
    );
    this.combatSystem.checkStomp(
      state, this.enemies, this.playerLogic, this.score, this.effectSystem, this.soundManager, level1Data.scoreStomp, this
    );

    // === STONE-NPC COLLISION ===
    this.combatSystem.checkStoneNpcCollision(this.npcs, this.score, this.effectSystem, this.playerLogic);

    // === 8. NPCs ===
    this.npcs.forEach(n => {
      const interactable = n.npc.category !== 'ambient';

      // Dead NPC: lie on the ground, no patrol, no interaction
      if (n.npc.killed) {
        this.characterRenderer.killNpc(n.sprite);
        // Killed over a gap and flagged to drown: sink into the sea and disappear
        if (n.fallsInSea && !n.sinking) {
          const overGap = !level1Data.groundPlatforms.some(
            p => n.npc.x >= (p as any).x && n.npc.x <= (p as any).x + (p as any).width
          );
          if (overGap) {
            n.sinking = true;
            this.tweens.add({
              targets: n.sprite,
              y: '+=80', alpha: 0, duration: 900,
              onComplete: () => { n.sprite.setVisible(false); },
            });
          }
        }
        return;
      }

      // Ambient NPCs walk back and forth a short distance. They bounce
      // at the edges of their patrol range so they never leave the level.
      n.npc.updatePatrol(delta, n.npc.patrolMinX, n.npc.patrolMaxX);

      const inRange = aabbOverlap(this.playerLogic.getHitbox(), n.npc.getHitbox());
      this.characterRenderer.updateNpcIndicator(n.sprite, interactable && inRange);

       // Update NPC procedural graphics position and animation
      this.characterRenderer.updateNpcGraphics(n.sprite, n.npc.x, n.npc.y, n.npc.type);

      // Check interaction
      if (interactable && inRange && !n.npc.hasBeenCollected()) {
        n.npc.collect(this.wallet);
        n.npc.markCollected();
        this.interactionSystem.registerInteract(n.npc.id);
        this.effectSystem.spawnParticles(n.npc.x, n.npc.y - n.npc.height / 2, 5, 0xffff00);
        this.score.addScore(n.npc.reward?.amount ?? 0);
        if (n.npc.reward) {
          this.powerupRenderer.spawnFloatingText(n.npc.x, n.npc.y - n.npc.height, `+${n.npc.reward.amount} ${n.npc.reward.type}`);
        }
      }
    });

    // === 9. Checkpoints ===
    this.checkpoints.forEach((cp, i) => {
      const c = level1Data.checkpoints[i];
      const cpW = (c as any).width ?? 40;
      const cpH = (c as any).height ?? 80;
      const cpHitbox = { x: cp.x - cpW / 2, y: cp.y - cpH, width: cpW, height: cpH };
      const playerHitbox = this.playerLogic.getHitbox();
      if (aabbOverlap(playerHitbox, cpHitbox) && !cp.isActive()) {
        cp.activate();
        this.playerLogic.updateCheckpoint(cp.x, cp.y);
        this.effectSystem.spawnParticles(cp.x, cp.y, 8, 0xffff00);
        this.soundManager.playCheckpoint();
      }
    });

    // === 10. Powerups collection ===
    this.powerups.forEach(p => {
      if (p.collected) return;
      const puData = POWERUP_DATA[p.type];
      const powerupHitbox = { x: p.x - (p.width ?? puData.width) / 2, y: p.worldY - (p.height ?? puData.height), width: p.width ?? puData.width, height: p.height ?? puData.height };
      const playerHitbox = this.playerLogic.getHitbox();
      if (aabbOverlap(playerHitbox, powerupHitbox)) {
        p.collected = true;
        (p as any).sprite.setVisible(false);
        this.interactionSystem.registerCollect(p.type);
        const effect = p.data ?? p.powerupData.effect;
        if (effect.type === 'currency') {
          this.wallet.add(effect.currency, effect.amount);
          this.score.addScore(effect.amount);
          this.powerupRenderer.spawnFlash(p.x, p.worldY, 0xffffff);
          this.effectSystem.spawnParticles(p.x, p.worldY, 5, 0xffffff);
          this.powerupRenderer.spawnFloatingText(p.x, p.worldY, `+${effect.amount} ${effect.currency}`);
          this.soundManager.playPickup();
        } else if (effect.type === 'health') {
          this.playerLogic.takeDamage(-effect.amount);
          this.effectSystem.spawnParticles(p.x, p.worldY, 5, 0x44ff44);
        } else if (effect.type === 'speedBoost') {
          this.playerLogic.applySpeedBoost(effect.duration);
          this.effectSystem.spawnParticles(p.x, p.worldY, 8, 0x00ffff);
          this.powerupRenderer.spawnFloatingText(p.x, p.worldY, `SPEED BOOST ${effect.duration / 1000}s`);
        } else if (effect.type === 'stones') {
          this.playerLogic.addStones(effect.amount);
          this.effectSystem.spawnParticles(p.x, p.worldY, 5, 0xcccccc);
          this.powerupRenderer.spawnFloatingText(p.x, p.worldY, `+${effect.amount} PIEDRAS`);
          this.soundManager.playPickup();
        }
      }
    });

    // === 11. Update visual stones ===
    this.combatSystem.updateVisualStones(delta, this, this.playerLogic);

    // === 12. Mission update ===
    const gameState: GameState = {
      walletValueInCup: this.wallet.valueInCup(),
      playerX: playerState.x,
      collectedItems: this.interactionSystem.getCollectedItems(),
      interactedTargets: new Set(this.interactionSystem.getInteractedTargets()),
    };
    this.missions.forEach(m => m.update(gameState));

    // === 13. HUD update ===
    this.hudRenderer.update(this.wallet, this.score, this.missions, playerState);

    // === 14. Victory/Defeat ===
    // Reaching the final checkpoint (exit marker) completes the level
    if (!this.exitTriggered && playerState.x >= level1Data.exitX - 20) {
      this.exitTriggered = true;
      this.soundManager.playVictory();
      this.scene.start(SCENE_KEYS.LEVEL_COMPLETE, {
        title: (level1Data as any).victoryTitle, text: (level1Data as any).victoryText, image: (level1Data as any).victoryImage,
        briefingTitle: (level1Data as any).briefingTitle, briefingText: (level1Data as any).briefingText, briefingImage: (level1Data as any).briefingImage,
      });
      return;
    }
    if (this.levelSystem.checkVictory(playerState, this.missions)) {
      this.soundManager.playVictory();
      this.scene.start(SCENE_KEYS.LEVEL_COMPLETE, {
        title: (level1Data as any).victoryTitle, text: (level1Data as any).victoryText, image: (level1Data as any).victoryImage,
        briefingTitle: (level1Data as any).briefingTitle, briefingText: (level1Data as any).briefingText, briefingImage: (level1Data as any).briefingImage,
      });
      return;
    }
    if (this.levelSystem.checkDefeat(playerState)) {
      this.soundManager.playDefeat();
      this.scene.start('DefeatScene', { title: (level1Data as any).defeatTitle, text: (level1Data as any).defeatText });
      return;
    }

    // === 15. Sync player rendering ===
    const newState = this.playerLogic.getState();
    this.characterRenderer.updatePlayer(this.playerSprite, {
      x: newState.x,
      y: newState.y,
      facing: newState.facing,
      vx: newState.vx,
      vy: newState.vy,
      isHurt: newState.isHurt,
      isInvulnerable: newState.isInvulnerable,
      invulnerabilityTimer: newState.invulnerabilityTimer,
      isDead: this.playerLogic.isDead(),
      stoneCooldown: newState.stoneCooldown,
      activeStones: newState.activeStones,
    });

    // Status text
    const statusText = `HP: ${newState.hp}/${newState.maxHp} | Piedras: ${newState.stones} | Pos: ${Math.round(newState.x)},${Math.round(newState.y)} | Suelo: ${newState.onGround} | Doble salto: ${newState.canDoubleJump ? 'SÍ' : 'NO'}`;
    this.statusText.setText(statusText);

    // === 16. Parallax background update ===
    const camX = this.cameras.main.scrollX;
    this.bgLayers.forEach(({ sprite, rate }) => {
      (sprite as any).tilePositionX = camX * rate;
    });
  }
}
